import ApiService from '../../../../../services/ApiService'

export default {
  name: 'ManageWorkHours',

  props: {
    unidadeId: {
      type: Number,
      required: true
    },
    workHourProp: {
      type: Object,
      default: null
    }
  },

  data: () => ({
    loading: false,
    valid: true,
    showErrors: false,
    isEditing: false,
    hasDescription: false,
    specificDate: null,
    selectedDays: [],
    workHour: {
      id: null,
      type: 'recorrente', // 'recorrente' ou 'especifico'
      startTime: '08:00',
      endTime: '18:00',
      description: '',
      officeWeekDays: [], // Array de DayOfWeek para recorrente
      specificDate: null, // Data para específico
    },
    daysOfWeek: [
      { label: 'Segunda-feira', value: 1 }, // Monday
      { label: 'Terça-feira', value: 2 },   // Tuesday
      { label: 'Quarta-feira', value: 3 },  // Wednesday
      { label: 'Quinta-feira', value: 4 },  // Thursday
      { label: 'Sexta-feira', value: 5 },   // Friday
      { label: 'Sábado', value: 6 },        // Saturday
      { label: 'Domingo', value: 0 },       // Sunday
    ],
    timeRules: [
      (v) => !!v || 'Horário é obrigatório',
      (v) => /^\d\d:\d\d$/.test(v) || 'Horário em formato inválido (HH:MM)',
      (v) => {
        const [hours, minutes] = v.split(':').map(Number)
        return (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) || 'Horário inválido'
      },
    ],
    apiService: new ApiService(),
  }),

  created() {
    if (this.workHourProp) {
      this.isEditing = true
      this.loadWorkHour(this.workHourProp)
    }
  },

  methods: {
    close() {
      this.$emit('close')
    },

    loadWorkHour(workHourData) {
      this.workHour.id = workHourData.id
      this.workHour.startTime = this.formatPartsToTime(workHourData.startHour, workHourData.startMinute) || '08:00'
      this.workHour.endTime = this.formatPartsToTime(workHourData.endHour, workHourData.endMinute) || '18:00'
      this.workHour.description = workHourData.message || ''
      this.hasDescription = !!workHourData.message

      // Se tem officeWeekDays, é recorrente
      if (workHourData.officeWeekDays && workHourData.officeWeekDays.length > 0) {
        this.workHour.type = 'recorrente'
        this.selectedDays = Array.from(workHourData.officeWeekDays)
      } 
      // Se tem specificDate, é específico
      else if (workHourData.date) {
        this.workHour.type = 'especifico'
        this.specificDate = new Date(workHourData.date)
      }
    },

    validate() {
      this.showErrors = true

      // Validação do formulário base
      if (!this.$refs.form.validate()) {
        return false
      }

      // Validação para recorrente: deve ter pelo menos um dia selecionado
      if (this.workHour.type === 'recorrente' && this.selectedDays.length === 0) {
        this.$toast.error('Selecione pelo menos um dia da semana')
        return false
      }

      // Validação para específico: deve ter uma data
      if (this.workHour.type === 'especifico' && !this.specificDate) {
        this.$toast.error('Selecione uma data específica')
        return false
      }

      // Validação de horários
      const [startHours, startMinutes] = this.workHour.startTime.split(':').map(Number)
      const [endHours, endMinutes] = this.workHour.endTime.split(':').map(Number)
      
      const startMinutesTotal = startHours * 60 + startMinutes
      const endMinutesTotal = endHours * 60 + endMinutes

      if (startMinutesTotal >= endMinutesTotal) {
        this.$toast.error('O horário de fim deve ser maior que o horário de início')
        return false
      }

      return true
    },

    async submit() {
      if (!this.validate()) {
        return
      }

      this.loading = true

      try {
        const [startHour, startMinute] = this.workHour.startTime.split(':').map(Number)
        const [endHour, endMinute] = this.workHour.endTime.split(':').map(Number)

        const payload = {
          UnidadeId: this.unidadeId,
          StartHour: startHour,
          StartMinute: startMinute,
          EndHour: endHour,
          EndMinute: endMinute,
          Date: this.workHour.type === 'especifico' ? this.formatDateToISO(this.specificDate) : null,
          Message: this.hasDescription ? this.workHour.description : null,
        }

        if (this.workHour.type === 'recorrente') {
          // Converter os dias selecionados para o formato do enum DayOfWeek
          payload.OfficeWeekDays = this.selectedDays.map(day => this.getDayOfWeekEnumValue(day))
          payload.Date = null
        } else {
          // Funcionamento específico
          payload.OfficeWeekDays = []
          payload.Date = this.formatDateToISO(this.specificDate)
        }

        let response

        if (this.isEditing) {
          payload.Id = this.workHour.id
          response = await this.apiService.post('UnitWorkHours/edit', payload)
        } else {
          response = await this.apiService.post('UnitWorkHours/add', payload)
        }

        if (response.statusCode === 200) {
          this.$toast.success(
            this.isEditing 
              ? 'Horário de funcionamento atualizado com sucesso!' 
              : 'Horário de funcionamento adicionado com sucesso!'
          )
          this.$emit('success')
          this.close()
        } else {
          this.$toast.error(response.message || 'Erro ao salvar horário de funcionamento')
        }
      } catch (error) {
        console.error('Erro ao salvar horário:', error)
        this.$toast.error(error.response?.data?.message || 'Erro ao salvar horário de funcionamento')
      } finally {
        this.loading = false
      }
    },

    getDayOfWeekEnumValue(dayValue) {
      // Mapeia o valor do checkbox para o enum DayOfWeek do C#
      // DayOfWeek: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
      const dayMap = {
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday'
      }
      
      return dayMap[dayValue]
    },

    formatPartsToTime(hour, minute) {
      if (hour === null || hour === undefined || minute === null || minute === undefined) {
        return null
      }
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    },

    formatDateToISO(date) {
      if (!date) return null
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      
      return `${year}-${month}-${day}T00:00:00`
    },
  },

  watch: {
    'workHour.type'(newType) {
      // Limpar seleções ao mudar o tipo
      if (newType === 'recorrente') {
        this.specificDate = null
      } else {
        this.selectedDays = []
      }
    },

    hasDescription(value) {
      if (!value) {
        this.workHour.description = ''
      }
    }
  }
}
